import { useEffect, useMemo, type ReactNode } from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { DEFAULT_THEME, ThemeContext, isTheme, type Theme } from '@/hooks/useTheme'

const STORAGE_KEY = 'prism-theme'

/** Browser chrome color per theme — mirrors each theme's --bg token (tokens.css). */
const THEME_META_COLOR: Record<Theme, string> = {
  dark: '#0B0B0D',
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [stored, setTheme] = useLocalStorage<string>(STORAGE_KEY, DEFAULT_THEME)
  // Browsers that used the old light/auto switcher still hold 'light' or
  // 'auto' here: anything that isn't a shipped theme falls back to the default.
  const theme: Theme = isTheme(stored) ? stored : DEFAULT_THEME

  // Apply data-theme to <html> so all tokens swap
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // Keep <meta name="theme-color"> in sync so Android's task switcher and
  // the browser chrome match the app surface.
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (!meta) return
    meta.content = THEME_META_COLOR[theme]
  }, [theme])

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
